import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Star, ChevronRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { firmInfo, services, serviceCategories, stats, testimonials, images } from '../data/mock';

const Home = () => {
  // Get first 2 services from each of 4 categories for preview
  const featuredCategories = serviceCategories.slice(0, 4);
  const featuredServices = featuredCategories.flatMap(cat => 
    services.filter(s => s.category === cat).slice(0, 2)
  ).slice(0, 8);

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon size={24} /> : <LucideIcons.FileText size={24} />;
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[100vh] sm:min-h-[90vh] flex items-center">
        {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://raw.githubusercontent.com/riya1234567/peeyush-assets/main/updated founter1.jpeg"
          alt="Professional CA Office"
          className="w-full h-full object-cover object-bottom"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-black/20 to-black/0"></div>
      </div>
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              <span className="text-emerald-300 text-sm font-medium">Trusted CA Firm in Agra | Since 2013</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Welcome to <br />
              <span className="text-white">{firmInfo.name}</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-emerald-300 font-medium mb-2">
              Your Trusted Chartered Accountants in Agra
            </p>
            
            <p className="text-lg text-amber-300 font-medium mb-4 italic">
              "{firmInfo.taglineHindi}"
            </p>
            
            <p className="text-gray-300 text-lg mb-8 max-w-2xl leading-relaxed">
              {firmInfo.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link to="/contact">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-emerald-500/25 transition-all">
                  Get Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Explore Services
                </Button>
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center md:text-left">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent z-10"></div>
      </section>

      {/* Taglines Section */}
      <section className="py-12 bg-emerald-700">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 text-white text-center">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-emerald-300" />
              <span className="font-medium">Empowering Your Finances with Trust and Integrity</span>
            </div>
            <div className="hidden md:block w-px h-6 bg-emerald-500"></div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-emerald-300" />
              <span className="font-medium">Expertise. Ethics. Excellence.</span>
            </div>
            <div className="hidden md:block w-px h-6 bg-emerald-500"></div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-emerald-300" />
              <span className="font-medium">Simplifying Compliance, Maximizing Growth</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">What We Offer</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">Our Professional Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive financial, taxation, and business advisory services tailored to meet your unique needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredServices.map((service) => (
              <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-md hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                    {getIcon(service.icon)}
                  </div>
                  <span className="text-xs text-emerald-600 font-medium uppercase tracking-wider">{service.category}</span>
                  <h3 className="text-lg font-semibold text-gray-900 mt-1 mb-2 group-hover:text-emerald-700 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" size="lg" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                View All Services
                <ChevronRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://raw.githubusercontent.com/riya1234567/peeyush-assets/main/company photo.jpeg"
                alt="Our founder" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-8 -right-8 bg-emerald-600 text-white p-6 rounded-2xl shadow-xl hidden md:block">
                <div className="text-4xl font-bold">{firmInfo.experience}</div>
                <div className="text-emerald-200">Years Experience</div>
              </div>
            </div>
            
            <div>
              <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">About Us</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6">
                Your Trusted Financial Partner in Agra
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {firmInfo.name} is a Chartered Accountancy firm headquartered in Agra, founded in {firmInfo.foundedYear}. We have been catering to clients across sectors, offering a comprehensive range of services tailored to meet the evolving financial and regulatory landscape.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                With over a decade of experience, our team is equipped with technical expertise and practical insights to deliver effective solutions, whether it be for individuals, startups, corporates, or NGOs.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                  <CheckCircle className="text-emerald-600 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-gray-900">Mission:</span>
                    <span className="text-gray-700 ml-2">{firmInfo.mission}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                  <CheckCircle className="text-emerald-600 flex-shrink-0" size={20} />
                  <div>
                    <span className="font-semibold text-gray-900">Vision:</span>
                    <span className="text-gray-700 ml-2">{firmInfo.vision}</span>
                  </div>
                </div>
              </div>

              <Link to="/about">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Learn More About Us
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Service Categories Section */}
      <section className="py-20 bg-emerald-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-emerald-300 font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">Complete Range of CA Services</h2>
            <p className="text-emerald-200 max-w-2xl mx-auto">
              From audit and taxation to company law compliance, we provide end-to-end financial solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceCategories.map((category, index) => {
              const categoryServices = services.filter(s => s.category === category);
              return (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all group">
                  <h3 className="text-xl font-bold text-white mb-4">{category}</h3>
                  <ul className="space-y-2">
                    {categoryServices.slice(0, 3).map((service) => (
                      <li key={service.id} className="flex items-center gap-2 text-emerald-200">
                        <CheckCircle size={16} className="text-emerald-400" />
                        <span>{service.title}</span>
                      </li>
                    ))}
                    {categoryServices.length > 3 && (
                      <li className="text-emerald-300 text-sm mt-2">+ {categoryServices.length - 3} more services</li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10">
                View All Services
                <ChevronRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">What Our Clients Say</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-6 italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <span className="text-emerald-700 font-semibold">
                        {testimonial.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-500">{testimonial.company}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-emerald-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-600 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Start Your Financial Journey?
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Get in touch with our expert team for a free consultation. We'll help you with all your financial and compliance needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${firmInfo.phone1}`}>
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg">
                  <Phone className="mr-2" size={20} />
                  Call: +91-{firmInfo.phone1}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Send Inquiry
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
